import { Icon } from "@iconify/react";
import { card, colors, type, viz } from "../styles";
import { PlaceholderChip } from "./Pills";

export type GhostShape = "line" | "columns" | "funnel" | "stats" | "none";

/**
 * The placeholder contract for this dashboard.
 *
 * A placeholder keeps the card's real title and its final size, so the finished
 * layout is visible now — but it shows a muted "Placeholder" chip (never a
 * status color; not working yet isn't an error), one line naming the exact
 * future source, a ghost frame with hairline axes and NO data marks, and a deep
 * link when the source already exists somewhere a human can open.
 *
 * The one hard rule: no invented numbers. A sample value rendered in a real
 * chart frame is indistinguishable from a measurement, and someone will
 * eventually quote it. The ghost frame is empty on purpose.
 */
const PlaceholderCard = ({
  title,
  source,
  shape = "columns",
  height = 180,
  link,
  linkLabel = "Open source",
  statLabels,
}: {
  title: string;
  /** The exact future source, e.g. "Mixpanel · KPIs · Texts Sent (insights 35370704)". */
  source: string;
  shape?: GhostShape;
  height?: number;
  link?: string;
  linkLabel?: string;
  /** For shape="stats": the figures this card will eventually show. */
  statLabels?: string[];
}) => (
  <div style={{ ...card, padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
      <div style={{ ...type.cardTitle, color: colors.inkMuted }}>{title}</div>
      <PlaceholderChip />
    </div>

    <GhostFrame shape={shape} height={height} statLabels={statLabels} />

    <div style={{ ...type.bodySmallRegular, color: colors.inkFaint }}>{source}</div>

    {link && (
      <a
        href={link}
        target="_blank"
        rel="noreferrer"
        style={{
          ...type.bodySmallMedium,
          color: colors.inkMuted,
          textDecoration: "none",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          alignSelf: "flex-start",
          border: `1px solid ${colors.border}`,
          borderRadius: 6,
          padding: "4px 10px",
        }}
      >
        {linkLabel}
        <Icon icon="tabler:arrow-up-right" height={13} />
      </a>
    )}
  </div>
);

/** Hairline axes only — deliberately no data marks of any kind. */
const GhostFrame = ({
  shape,
  height,
  statLabels,
}: {
  shape: GhostShape;
  height: number;
  statLabels?: string[];
}) => {
  if (shape === "none") return null;

  // Names the figures that will land here, with a dashed rule standing in for
  // each value. Still no numbers — just the shape of what's coming.
  if (shape === "stats") {
    return (
      <div
        style={{
          background: colors.bg,
          borderRadius: 8,
          border: `1px dashed ${colors.border}`,
          padding: 14,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {(statLabels ?? []).map(label => (
          <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ ...type.bodySmallMedium, color: colors.inkFaint }}>{label}</span>
            <span
              style={{
                height: 2,
                width: 88,
                borderTop: `2px dashed ${viz.ghost}`,
              }}
            />
          </div>
        ))}
      </div>
    );
  }

  const axisStyle = { stroke: viz.axis, strokeWidth: 1 } as const;
  const ghostStyle = { stroke: viz.ghost, strokeWidth: 1, strokeDasharray: "3 4" } as const;

  return (
    <div
      style={{
        height,
        background: colors.bg,
        borderRadius: 8,
        border: `1px dashed ${colors.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 300 120"
        preserveAspectRatio="none"
        aria-label="No data yet"
        role="img"
      >
        {/* Recessive gridlines, then the two real axes. */}
        {[30, 60, 90].map(y => (
          <line key={y} x1={28} y1={y} x2={296} y2={y} {...ghostStyle} />
        ))}
        <line x1={28} y1={4} x2={28} y2={112} {...axisStyle} />
        <line x1={28} y1={112} x2={296} y2={112} {...axisStyle} />

        {shape === "funnel" &&
          [0, 1, 2].map(step => (
            <rect
              key={step}
              x={32}
              y={12 + step * 34}
              width={240 - step * 60}
              height={22}
              rx={4}
              fill="none"
              {...ghostStyle}
            />
          ))}
      </svg>
    </div>
  );
};

export default PlaceholderCard;
